import { startCase } from "lodash";
import type { IVideo } from "../../pages/admin/videos/hooks/useFetchVideos";
import { labelChannelEntries } from "../../pages/videoProduction/helper";

/**
 * The read-side of a deliverable's project metadata, shared by the video
 * card and the video details header so both spell a channel, a content type
 * and an option selection the same way.
 */

export interface VideoOptionGroup {
  optionId: string;
  optionName: string;
  displayValue: string;
}

/**
 * The project's additional options this deliverable was cut for — one group
 * per option ("Watermarks: TDC Watermark", "Subtitle Languages: English").
 * Most videos carry none: a project only gets option-linked files when it asks
 * for extra watermark or caption variants, and each variant maps to its own
 * S3 file through `option_selection_links`.
 */
export const groupVideoOptions = (video: IVideo | null | undefined): VideoOptionGroup[] => {
  const links = video?.option_selection_links || [];
  if (!links.length) return [];
  const grouped: Record<string, { optionName: string; type?: string; items: any[] }> = {};
  links.forEach((link) => {
    const selection = link?.selection;
    const option = selection?.option_value?.option;
    if (!option?.id || !option?.name) return;
    const key = String(option.id);
    if (!grouped[key]) grouped[key] = { optionName: option.name, type: option.type, items: [] };
    grouped[key].items.push(selection);
  });
  return Object.entries(grouped)
    .map(([optionId, { optionName, type, items }]) => {
      let displayValue = "";
      if (type === "value") {
        displayValue = [...items]
          .sort((a, b) => Number(a?.numeric_value) - Number(b?.numeric_value))
          .map((i) => i?.numeric_value)
          .filter((n) => n !== null && n !== undefined && n !== "")
          .join(" - ");
      } else {
        displayValue = items
          .map((i) => i?.option_value?.name)
          .filter((n) => n != null && n !== "")
          .join(", ");
      }
      return { optionId, optionName, displayValue };
    })
    .filter((g) => g.displayValue);
};

/**
 * Only the options that tell one variant from its siblings. Every variant
 * carries a link per option, defaults included, so lists that put sibling
 * variants side by side (the apply-comment picker, the "Applied to" menu)
 * drop the default values and keep "Spanish", not "English".
 */
export const groupVariantOptions = (video: IVideo | null | undefined): VideoOptionGroup[] =>
  video
    ? groupVideoOptions({
        ...video,
        option_selection_links: (video.option_selection_links || []).filter(
          (l) => l?.selection?.option_value?.default === false,
        ),
      })
    : [];

/**
 * A card's `project` prop when the feed does not hydrate `video.project`
 * itself (the Downloads tab passes the full project; the video feeds nest a
 * trimmed one on each row). `content_channels` is the project endpoints'
 * derived per-instance list; `project_channels` is the raw instance list the
 * video feeds attach.
 */
export interface VideoProjectContext {
  content_type?: { id?: number; name?: string } | null;
  project_channels?: Array<{
    id: number;
    label?: string | null;
    content_channel?: { id?: number; name?: string } | null;
  }>;
  content_channels?: Array<{
    name?: string;
    brand_project_channel_id?: number | null;
    instance_label?: string | null;
  }>;
}

/** "product_review" → "Product Review", the way the project list spells it. */
export const getVideoContentTypeLabel = (
  video: IVideo | null | undefined,
  project?: VideoProjectContext | null,
): string | null => {
  const name = video?.project?.content_type?.name || project?.content_type?.name;
  return name ? startCase(name.replace(/_/g, " ")) : null;
};

/**
 * The channel this deliverable belongs to, numbered like the Downloads tab
 * when the project holds the same channel more than once ("PDP #2"). Falls
 * back to the generic channel name for legacy files that only carry
 * `content_channel_id`.
 */
export const getVideoChannelLabel = (
  video: IVideo | null | undefined,
  project?: VideoProjectContext | null,
): string | null => {
  if (!video) return null;
  const instanceId = video.brand_project_channel_id;
  if (instanceId != null) {
    const raw = video.project?.project_channels || project?.project_channels;
    // Raw instances: sort by id and number duplicates in that order.
    const instances = raw
      ? raw
          .slice()
          .sort((a, b) => Number(a.id) - Number(b.id))
          .map((inst) => ({
            id: inst.id,
            name: inst.content_channel?.name,
            instance_label: inst.label,
          }))
      : // Derived entries arrive already sorted by instance id server-side.
        (project?.content_channels || []).map((ch) => ({
          id: ch.brand_project_channel_id as number,
          name: ch.name,
          instance_label: ch.instance_label,
        }));
    const match = labelChannelEntries(instances).find((inst) => inst.id === instanceId);
    if (match) return match.label;
  }
  return video.content_channel?.name || null;
};

/**
 * "30 fps" / "29.97 fps" / "23.976 fps" from the probed `s3_files.fps`, or ""
 * when the file predates the probe. Up to three decimals, trailing zeros
 * dropped, so a whole rate never reads "30.000".
 */
export const formatFps = (fps: number | string | null | undefined): string => {
  const n = Number(fps);
  if (fps == null || fps === '' || !Number.isFinite(n) || n <= 0) return '';
  return `${parseFloat(n.toFixed(3))} fps`;
};

/** "4 KB" / "18 KB" / "1.2 MB". Returns "" when the size is unknown. */
export const formatFileSize = (bytes?: number | null): string => {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
};

/**
 * The probed `s3_files.file_size` (the iconik source file's bytes; BIGINT, so
 * pg sends it as a string), or "" when the file predates the probe.
 */
export const formatVideoFileSize = (size: number | string | null | undefined): string => {
  const n = Number(size);
  if (size == null || size === '' || !Number.isFinite(n) || n <= 0) return '';
  return formatFileSize(n);
};
